import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Music2, MapPin, Headphones, Building2, Users } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import api from '../lib/api';

export default function DJsListingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  // Default tab based on route: /agencias opens the Agencias tab
  const initialTab = location.pathname.startsWith('/agencias') ? 'agencies' : 'djs';
  const [tab, setTab] = useState(initialTab);

  const [djs, setDjs] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [loadingDjs, setLoadingDjs] = useState(true);
  const [loadingAgencies, setLoadingAgencies] = useState(true);
  const [query, setQuery] = useState('');
  const [genreFilter, setGenreFilter] = useState('all');

  // Fetch DJs (only real DJs — agencies are excluded backend-side)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get('/public/djs?limit=200');
        if (mounted) setDjs(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (mounted) setDjs([]);
      } finally {
        if (mounted) setLoadingDjs(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Fetch agencies (alphabetical, includes roster counts)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get('/public/agencies?limit=200');
        if (mounted) setAgencies(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (mounted) setAgencies([]);
      } finally {
        if (mounted) setLoadingAgencies(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Keep the URL in sync with the active tab so agencies can share /agencias
  const switchTab = (next) => {
    setTab(next);
    setQuery('');
    if (next === 'agencies' && location.pathname !== '/agencias') {
      navigate('/agencias', { replace: false });
    } else if (next === 'djs' && location.pathname !== '/djs') {
      navigate('/djs', { replace: false });
    }
  };

  const genres = useMemo(() => {
    const set = new Set();
    djs.forEach((d) => (d.genres || []).forEach((g) => g && set.add(g)));
    return ['all', ...Array.from(set).sort()];
  }, [djs]);

  const filteredDjs = useMemo(() => {
    const q = query.trim().toLowerCase();
    return djs.filter((d) => {
      if (genreFilter !== 'all' && !(d.genres || []).includes(genreFilter)) return false;
      if (!q) return true;
      const hay = [d.name, d.city, ...(d.genres || [])].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [djs, query, genreFilter]);

  const filteredAgencies = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return agencies;
    return agencies.filter((a) => {
      const hay = [a.name, a.city, a.country].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [agencies, query]);

  const showingAgencies = tab === 'agencies';

  return (
    <div
      className="min-h-screen bg-[#050505] text-white"
      style={{ fontFamily: "'Manrope', sans-serif" }}
      data-testid="djs-listing-page"
    >
      <SEO
        title={showingAgencies ? 'Agencias · Bravo' : 'DJs · Bravo'}
        description={showingAgencies
          ? 'Descubrí las agencias de DJs en Bravo. Rosters, contactos y bookings.'
          : 'Explorá los DJs en órbita en Argentina. Bravo Platform.'}
        url={showingAgencies ? '/agencias' : '/djs'}
      />
      <Navbar />

      {/* Header */}
      <section className="pt-12 sm:pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <p
            className="text-[10px] sm:text-xs uppercase tracking-[0.3em] font-bold text-[#D4AF37] mb-3"
            style={{ fontFamily: "'Unbounded', sans-serif" }}
          >
            {showingAgencies ? 'Agencias en órbita' : 'DJs en órbita'}
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] uppercase max-w-3xl"
            style={{ fontFamily: "'Unbounded', sans-serif" }}
          >
            {showingAgencies ? 'Las que mueven la escena' : 'Quiénes están sonando'}
            <span className="text-[#D4AF37]">.</span>
          </h1>
          <p className="mt-5 text-white/55 max-w-xl text-sm sm:text-base">
            {showingAgencies
              ? 'Agencias que representan a los artistas argentinos. Tocá una para ver su roster completo.'
              : 'Artistas que dan forma a la noche argentina. Tocá un nombre para abrir su press kit.'}
          </p>
        </div>
      </section>

      {/* Tabs */}
      <section className="px-4 sm:px-6 lg:px-8 pb-4">
        <div className="max-w-7xl mx-auto flex items-center gap-2 border-b border-white/10">
          <TabBtn active={tab === 'djs'} onClick={() => switchTab('djs')} icon={Headphones} label="DJs" count={djs.length} testId="tab-djs" />
          <TabBtn active={tab === 'agencies'} onClick={() => switchTab('agencies')} icon={Building2} label="Agencias" count={agencies.length} testId="tab-agencies" />
        </div>
      </section>

      {/* Filters */}
      <section className="px-4 sm:px-6 lg:px-8 pb-6 pt-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={showingAgencies ? 'Buscar agencia o ciudad...' : 'Buscar DJ, ciudad o género...'}
              className="w-full bg-white/[0.04] border border-white/10 rounded-full pl-11 pr-4 py-3 text-sm placeholder-white/30 focus:outline-none focus:border-white/30"
              data-testid={showingAgencies ? 'agencies-search-input' : 'djs-search-input'}
            />
          </div>
          {!showingAgencies && (
            <div className="flex gap-2 overflow-x-auto pb-1 sm:pb-0">
              {genres.slice(0, 8).map((g) => (
                <button
                  key={g}
                  onClick={() => setGenreFilter(g)}
                  className={`flex-shrink-0 text-[11px] uppercase tracking-wider font-bold px-3 py-2 rounded-full border transition-colors ${
                    genreFilter === g
                      ? 'bg-[#D4AF37] text-black border-[#D4AF37]'
                      : 'bg-white/[0.04] text-white/60 border-white/10 hover:border-white/30'
                  }`}
                  style={{ fontFamily: "'Unbounded', sans-serif" }}
                  data-testid={`djs-genre-${g}`}
                >
                  {g === 'all' ? 'Todos' : g}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Grid */}
      <section className="px-4 sm:px-6 lg:px-8 pb-24">
        <div className="max-w-7xl mx-auto">
          {showingAgencies ? (
            loadingAgencies ? (
              <SkeletonGrid rounded="rounded-2xl" testId="agencies-loading" />
            ) : filteredAgencies.length === 0 ? (
              <EmptyState icon={Building2} testId="agencies-empty" msg="Sin resultados." />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="agencies-grid">
                {filteredAgencies.map((a, idx) => (
                  <AgencyCard key={a.id} agency={a} delay={idx * 0.03} />
                ))}
              </div>
            )
          ) : (
            loadingDjs ? (
              <SkeletonGrid rounded="rounded-full" testId="djs-loading" />
            ) : filteredDjs.length === 0 ? (
              <EmptyState icon={Headphones} testId="djs-empty" msg="Sin resultados." />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="djs-grid">
                {filteredDjs.map((dj, idx) => (
                  <DJTile key={dj.id} dj={dj} delay={idx * 0.03} />
                ))}
              </div>
            )
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, count, testId }) {
  return (
    <button
      onClick={onClick}
      className={`relative flex items-center gap-2 px-4 sm:px-5 py-3 text-sm font-bold uppercase tracking-wider transition-colors ${
        active ? 'text-white' : 'text-white/40 hover:text-white/70'
      }`}
      style={{ fontFamily: "'Unbounded', sans-serif" }}
      data-testid={testId}
    >
      <Icon size={16} />
      <span>{label}</span>
      <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${active ? 'bg-[#D4AF37] text-black' : 'bg-white/10 text-white/60'}`}>
        {count}
      </span>
      {active && (
        <motion.span
          layoutId="djs-tab-underline"
          className="absolute left-0 right-0 -bottom-px h-[2px] bg-[#D4AF37]"
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
        />
      )}
    </button>
  );
}

function SkeletonGrid({ rounded, testId }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid={testId}>
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className={`aspect-square ${rounded} bg-white/[0.03] animate-pulse`} />
      ))}
    </div>
  );
}

function EmptyState({ icon: Icon, msg, testId }) {
  return (
    <div className="text-center py-20 border border-dashed border-white/10 rounded-2xl" data-testid={testId}>
      <Icon className="mx-auto text-white/20 mb-4" size={36} />
      <p className="text-white/40 text-sm">{msg}</p>
    </div>
  );
}

function DJTile({ dj, delay = 0 }) {
  const genres = (dj.genres || []).slice(0, 2);
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link
        to={`/dj/${dj.slug || dj.id}`}
        className="group block"
        data-testid={`dj-tile-${dj.id}`}
      >
        <div className="aspect-square rounded-full overflow-hidden bg-[#0A0A0A] border border-white/10 group-hover:border-[#D4AF37]/60 transition-colors duration-500">
          {dj.photo ? (
            <img
              src={dj.photo}
              alt={dj.name}
              className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-[1.04] transition-all duration-700"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/20">
              <Music2 size={36} />
            </div>
          )}
        </div>
        <div className="mt-4 px-1 text-center">
          <h3
            className="text-base sm:text-lg font-extrabold text-white truncate group-hover:text-[#D4AF37] transition-colors"
            style={{ fontFamily: "'Unbounded', sans-serif" }}
          >
            {dj.name}
          </h3>
          {genres.length > 0 && (
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 mt-1 truncate">
              {genres.join(' · ')}
            </p>
          )}
          {dj.city && (
            <p className="text-[10px] text-white/30 mt-1 inline-flex items-center gap-1">
              <MapPin size={9} /> {dj.city}
            </p>
          )}
        </div>
      </Link>
    </motion.div>
  );
}

function AgencyCard({ agency, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link
        to={`/agencia/${agency.slug || agency.id}`}
        className="group block bg-[#0A0A0A] border border-white/10 rounded-2xl overflow-hidden hover:border-[#D4AF37]/60 transition-colors"
        data-testid={`agency-card-${agency.id}`}
      >
        {/* Banner strip */}
        <div className="relative h-24 sm:h-28 bg-gradient-to-br from-[#D4AF37]/20 via-[#121212] to-[#030303] overflow-hidden">
          {agency.banner && (
            <img src={agency.banner} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" loading="lazy" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#D4AF37] text-black text-[9px] font-black uppercase tracking-widest">
            <Building2 size={10} /> Agencia
          </span>
        </div>

        <div className="p-4 sm:p-5 flex items-start gap-4 -mt-8 sm:-mt-10 relative">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl overflow-hidden border-2 border-[#D4AF37]/50 bg-[#121212] flex items-center justify-center flex-shrink-0 shadow-lg">
            {agency.photo ? (
              <img src={agency.photo} alt={agency.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
            ) : (
              <Building2 size={28} className="text-[#D4AF37]" />
            )}
          </div>
          <div className="min-w-0 flex-1 pt-6 sm:pt-8">
            <h3
              className="text-lg sm:text-xl font-extrabold text-white truncate group-hover:text-[#D4AF37] transition-colors"
              style={{ fontFamily: "'Unbounded', sans-serif" }}
            >
              {agency.name}
            </h3>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-white/40 mt-1">
              {agency.city && (
                <span className="inline-flex items-center gap-1"><MapPin size={10} /> {agency.city}</span>
              )}
              <span className="inline-flex items-center gap-1 text-[#D4AF37]">
                <Users size={10} /> {agency.roster_count} DJ{agency.roster_count === 1 ? '' : 's'} en el roster
              </span>
            </div>
          </div>
        </div>

        {agency.tagline && (
          <p className="px-4 sm:px-5 pb-5 text-xs text-white/50 line-clamp-2 leading-relaxed">
            {agency.tagline}
          </p>
        )}
      </Link>
    </motion.div>
  );
}
